import React from 'react';
import { motion } from 'framer-motion';
import { 
  CurrencyDollarIcon,
  UserGroupIcon,
  ClockIcon,
  CheckCircleIcon,
  ArrowTrendingUpIcon,
  XCircleIcon,
  CalendarIcon
} from '@heroicons/react/24/outline';
import { Doughnut, Line } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, 
  LinearScale, PointElement, LineElement, Title } from 'chart.js';
import { 
  formatRevenue, 
  formatGrowth, 
  formatPtBrNumber, 
  calculatePercentage,
  getTrendColor
} from '../../../utils';

// Registrar os componentes necessários do Chart.js
ChartJS.register(
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale, 
  LinearScale, 
  PointElement, 
  LineElement, 
  Title
);

const OverviewSection = ({ metrics, appointments, isMobile }) => {
  // Dados formatados para gráficos de visão geral
  const completionData = {
    labels: ['Realizados', 'Cancelados', 'Não compareceu'],
    datasets: [
      {
        data: [
          metrics?.completionRate || 70, 
          metrics?.cancellationRate || 20, 
          metrics?.noShowRate || 10
        ],
        backgroundColor: [
          'rgba(34, 197, 94, 0.7)',  // verde
          'rgba(249, 115, 22, 0.7)', // laranja 
          'rgba(239, 68, 68, 0.7)',  // vermelho
        ],
        borderColor: [
          'rgba(34, 197, 94, 1)',
          'rgba(249, 115, 22, 1)',
          'rgba(239, 68, 68, 1)',
        ],
        borderWidth: 1,
      },
    ],
  };

  const revenueChartData = {
    labels: metrics?.revenueData?.data?.map(item => item.label) || 
            ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun'],
    datasets: [
      {
        label: 'Receita',
        data: metrics?.revenueData?.data?.map(item => item.value) || 
              [1200, 1900, 1500, 2200, 2500, 2800],
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        tension: 0.4,
        fill: true
      }
    ]
  };

  // Configuração dos gráficos
  const doughnutOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          font: {
            size: isMobile ? 10 : 12
          }
        }
      }
    },
    cutout: '70%',
  };

  const lineOptions = {
    responsive: true,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        callbacks: {
          label: function(context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label += formatRevenue(context.parsed.y);
            }
            return label;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function(value) {
            return formatRevenue(value);
          }
        }
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.1,
        duration: 0.5
      }
    })
  };

  // Calcular métricas e tendências
  const revenueChange = metrics?.revenueChange || 5;
  const revenueChangeColor = getTrendColor(revenueChange);
  
  const appointmentsChange = metrics?.appointmentsChange || 12;
  const appointmentsChangeColor = getTrendColor(appointmentsChange);
  
  const retentionChange = metrics?.retentionChange || 3;
  const retentionChangeColor = getTrendColor(retentionChange);
  
  const completionRateChange = metrics?.completionRateChange || 2;
  const completionRateChangeColor = getTrendColor(completionRateChange);

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-bold text-gray-800">Visão Geral</h2>
      
      {/* Cards de Métricas Principais */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <motion.div 
          custom={0}
          initial="hidden"
          animate="visible"
          variants={cardVariants}
          whileHover={{ scale: 1.02 }}
          className="bg-white rounded-xl p-4 shadow-sm border border-gray-100"
        >
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-gray-500">Receita Total</h3>
            <div className="bg-blue-100 p-2 rounded-lg">
              <CurrencyDollarIcon className="h-5 w-5 text-blue-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-800">
            {formatRevenue(metrics?.totalRevenue || 0)}
          </p>
          <div className="mt-2 flex items-center text-xs">
            <ArrowTrendingUpIcon className={`h-3 w-3 text-${revenueChangeColor}-500 mr-1`} />
            <span className={`text-${revenueChangeColor}-500 font-medium`}>
              {formatGrowth(revenueChange)}
            </span>
            <span className="text-gray-400 ml-1">vs. período anterior</span>
          </div>
        </motion.div>

        <motion.div 
          custom={1}
          initial="hidden"
          animate="visible"
          variants={cardVariants}
          whileHover={{ scale: 1.02 }}
          className="bg-white rounded-xl p-4 shadow-sm border border-gray-100"
        >
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-gray-500">Agendamentos</h3>
            <div className="bg-purple-100 p-2 rounded-lg">
              <CalendarIcon className="h-5 w-5 text-purple-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-800">
            {formatPtBrNumber(metrics?.totalAppointments || 0)}
          </p>
          <div className="mt-2 flex items-center text-xs">
            <ArrowTrendingUpIcon className={`h-3 w-3 text-${appointmentsChangeColor}-500 mr-1`} />
            <span className={`text-${appointmentsChangeColor}-500 font-medium`}>
              {formatGrowth(appointmentsChange)}
            </span>
            <span className="text-gray-400 ml-1">vs. período anterior</span>
          </div>
        </motion.div>

        <motion.div 
          custom={2}
          initial="hidden"
          animate="visible"
          variants={cardVariants}
          whileHover={{ scale: 1.02 }}
          className="bg-white rounded-xl p-4 shadow-sm border border-gray-100"
        >
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-gray-500">Retenção</h3>
            <div className="bg-amber-100 p-2 rounded-lg">
              <UserGroupIcon className="h-5 w-5 text-amber-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-800">
            {formatPtBrNumber(metrics?.clientRetention || 0)}%
          </p>
          <div className="mt-2 flex items-center text-xs">
            <ArrowTrendingUpIcon className={`h-3 w-3 text-${retentionChangeColor}-500 mr-1`} />
            <span className={`text-${retentionChangeColor}-500 font-medium`}>
              {formatGrowth(retentionChange)}
            </span>
            <span className="text-gray-400 ml-1">vs. período anterior</span>
          </div>
        </motion.div>

        <motion.div 
          custom={3}
          initial="hidden"
          animate="visible"
          variants={cardVariants}
          whileHover={{ scale: 1.02 }}
          className="bg-white rounded-xl p-4 shadow-sm border border-gray-100"
        >
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-gray-500">Conclusão</h3>
            <div className="bg-green-100 p-2 rounded-lg">
              <CheckCircleIcon className="h-5 w-5 text-green-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-800">
            {formatPtBrNumber(metrics?.completionRate || 0)}%
          </p>
          <div className="mt-2 flex items-center text-xs">
            <ArrowTrendingUpIcon className={`h-3 w-3 text-${completionRateChangeColor}-500 mr-1`} />
            <span className={`text-${completionRateChangeColor}-500 font-medium`}>
              {formatGrowth(completionRateChange)}
            </span>
            <span className="text-gray-400 ml-1">vs. período anterior</span>
          </div>
        </motion.div>
      </div>

      {/* Gráficos */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-xl p-4 shadow-sm border border-gray-100"
        >
          <h3 className="text-lg font-semibold mb-4">Status dos Agendamentos</h3>
          <div className="h-64 flex items-center justify-center">
            <Doughnut data={completionData} options={doughnutOptions} />
          </div>
          <div className="mt-2 grid grid-cols-3 gap-2 text-center">
            <div>
              <div className="flex items-center justify-center">
                <CheckCircleIcon className="h-4 w-4 text-green-500 mr-1" />
                <span className="text-xs font-medium">Realizados</span>
              </div>
              <p className="text-sm font-bold">{metrics?.completionRate || 70}%</p>
            </div>
            <div>
              <div className="flex items-center justify-center">
                <XCircleIcon className="h-4 w-4 text-orange-500 mr-1" />
                <span className="text-xs font-medium">Cancelados</span>
              </div>
              <p className="text-sm font-bold">{metrics?.cancellationRate || 20}%</p>
            </div>
            <div>
              <div className="flex items-center justify-center">
                <XCircleIcon className="h-4 w-4 text-red-500 mr-1" />
                <span className="text-xs font-medium">No-shows</span>
              </div>
              <p className="text-sm font-bold">{metrics?.noShowRate || 10}%</p>
            </div>
          </div>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white rounded-xl p-4 shadow-sm border border-gray-100"
        >
          <h3 className="text-lg font-semibold mb-4">Receita ao Longo do Tempo</h3>
          <div className="h-64">
            <Line data={revenueChartData} options={lineOptions} />
          </div>
          <div className="mt-2 grid grid-cols-3 gap-2 text-center">
            <div>
              <p className="text-xs text-gray-500">Média</p>
              <p className="text-sm font-bold">
                {formatRevenue((metrics?.totalRevenue || 6000) / (metrics?.totalAppointments || 20))}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Melhor mês</p>
              <p className="text-sm font-bold">{revenueChartData.labels[revenueChartData.datasets[0].data.indexOf(Math.max(...revenueChartData.datasets[0].data))]}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Tendência</p>
              <p className="text-sm font-bold text-green-500">{formatGrowth(metrics?.growthRate || 15)}</p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default OverviewSection; 